import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { userAPI } from '../../lib/api';
import { Button } from '../../components/ui/button';
import { LoadingSpinner } from '../../components/LoadingSpinner';
import { BannerAdPlaceholder } from '../../components/BannerAdPlaceholder';
import { RewardedGate } from '../../components/RewardedGate';
import { Clock, CheckCircle, XCircle, ArrowLeft, ArrowRight } from 'lucide-react';
import { toast } from 'sonner';
import { formatTime } from '../../lib/utils';
import { motion, AnimatePresence } from 'framer-motion';
import { useTranslation } from 'react-i18next';
import { apiErrorMessage } from '../../lib/apiErrors';

// Helper to resolve image URLs (handles both relative and absolute URLs)
const resolveImageUrl = (url) => {
  if (!url) return null;
  if (url.startsWith('http://') || url.startsWith('https://')) return url;
  const backendUrl = process.env.REACT_APP_BACKEND_URL || '';
  return `${backendUrl}${url}`;
};

export function QuizPage() {
  const { quizIndex } = useParams();
  const navigate = useNavigate();
  const { t, i18n } = useTranslation();
  
  const [quiz, setQuiz] = useState(null);
  const [currentQuestion, setCurrentQuestion] = useState(0);
  const [answers, setAnswers] = useState({});
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [startTime, setStartTime] = useState(null);
  const [questionTimer, setQuestionTimer] = useState(30);
  const [showAdGate, setShowAdGate] = useState(false);
  
  // Timer: 30 seconds per question
  const QUESTION_TIME_LIMIT = 30; // seconds

  useEffect(() => {
    loadQuiz();
  }, [quizIndex]);

  useEffect(() => {
    if (!quiz || submitting) return;
    
    // Reset timer when question changes
    setQuestionTimer(QUESTION_TIME_LIMIT);
    
    const timer = setInterval(() => {
      setQuestionTimer((prev) => {
        if (prev <= 1) {
          handleTimeExpired();
          return QUESTION_TIME_LIMIT;
        }
        return prev - 1;
      });
    }, 1000);
    
    return () => clearInterval(timer);
  }, [currentQuestion, quiz, submitting]);

  const loadQuiz = async () => {
    try {
      // Extract base language code (e.g., "en-US" -> "en", "sk-SK" -> "sk")
      const baseLang = i18n.language?.split('-')[0]?.split('@')[0] || 'en';
      const lang = ['en', 'sk'].includes(baseLang) ? baseLang : 'en';
      const res = await userAPI.getQuiz(quizIndex, lang);
      
      // Check if retry requires ad
      if (res.data.requires_ad) {
        setShowAdGate(true);
        setQuiz(res.data);
      } else {
        setQuiz(res.data);
        setStartTime(Date.now());
      }
    } catch (error) {
      // Handle Pydantic validation errors (array of objects) or simple string detail
      toast.error(apiErrorMessage(error, t, 'quiz.loadFailed'));
      navigate('/home');
    } finally {
      setLoading(false);
    }
  };

  const handleAnswer = (questionId, optionKey) => {
    // Update answers state
    const newAnswers = {
      ...answers,
      [questionId]: optionKey
    };
    setAnswers(newAnswers);
    
    // Auto-advance to next question after answering
    setTimeout(() => {
      if (currentQuestion < quiz.questions.length - 1) {
        setCurrentQuestion(currentQuestion + 1);
      } else {
        // Last question - auto submit with the updated answers
        handleSubmitWithAnswers(newAnswers);
      }
    }, 500); // Small delay for animation
  };

  const handleTimeExpired = () => {
    // Time expired - move to next question or submit
    if (currentQuestion < quiz.questions.length - 1) {
      setCurrentQuestion(currentQuestion + 1);
      toast.warning(t('quiz.timeExpiredNext', 'Time expired! Moving to next question.'));
    } else {
      // Last question - auto submit with current answers
      toast.warning(t('quiz.timeExpiredSubmit', 'Time expired! Auto-submitting the assessment.'));
      handleSubmitWithAnswers(answers);
    }
  };

  const handleNext = () => {
    if (currentQuestion < quiz.questions.length - 1) {
      setCurrentQuestion(currentQuestion + 1);
    }
  };

  const handlePrevious = () => {
    if (currentQuestion > 0) {
      setCurrentQuestion(currentQuestion - 1);
    }
  };

  // Submit with explicit answers object (to avoid race condition)
  const handleSubmitWithAnswers = async (answersToSubmit) => {
    const timeMs = Date.now() - startTime;

    setSubmitting(true);
    try {
      const submissionAnswers = quiz.questions.map(q => ({
        question_id: q._id,
        choice_key: answersToSubmit[q._id] || 'UNANSWERED'
      }));
      
      console.log('📝 Submitting quiz with answers:', submissionAnswers.filter(a => a.choice_key !== 'UNANSWERED').length, 'answered');
      
      const res = await userAPI.submitQuiz(quizIndex, {
        answers: submissionAnswers,
        time_ms: timeMs
      });
      
      toast.success(t('quiz.submitted', 'Assessment submitted!'));
      
      // Navigate directly to results - NO AD HERE
      // Ad will show when user clicks "Show correct answers" or starts next quiz
      navigate(`/results/${quizIndex}`, { state: { result: res.data } });
    } catch (error) {
      toast.error(apiErrorMessage(error, t, 'quiz.submitFailed'));
    } finally {
      setSubmitting(false);
    }
  };

  // Legacy submit function (uses current state)
  const handleSubmit = async () => {
    await handleSubmitWithAnswers(answers);
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-20">
        <LoadingSpinner />
      </div>
    );
  }

  // Show ad gate for retry (not for initial quiz start)
  if (showAdGate) {
    return (
      <RewardedGate
        onComplete={() => {
          setShowAdGate(false);
          setStartTime(Date.now());
        }}
        onCancel={() => navigate('/home')}
        title={t('quiz.retryRequired')}
        description={t('quiz.watchAdToRetry')}
      />
    );
  }

  if (!quiz) {
    return <div className="text-center py-20 text-slate-400">{t('quiz.notAvailable', 'Assessment not available')}</div>;
  }

  const currentQ = quiz.questions[currentQuestion];
  const progress = ((currentQuestion + 1) / quiz.questions.length) * 100;

  return (
    <div className="max-w-2xl mx-auto space-y-4">
      {/* Progress Header */}
      <div className="bg-slate-800/50 backdrop-blur rounded-2xl p-4 border border-slate-700/50">
        <div className="flex items-center justify-between mb-3">
          <span className="text-sm font-medium text-slate-400">
            {t('quiz.question')} {currentQuestion + 1}/{quiz.questions.length}
          </span>
          <div className={`flex items-center gap-2 px-3 py-1 rounded-full ${
            questionTimer <= 10 ? 'bg-red-500/20 text-red-400' : 'bg-slate-700 text-slate-300'
          }`}>
            <Clock className="w-4 h-4" />
            <span className="font-mono font-bold">{questionTimer}s</span>
          </div>
        </div>
        <div className="h-2 bg-slate-700 rounded-full overflow-hidden">
          <motion.div 
            className="h-full bg-gradient-to-r from-teal-500 to-cyan-500"
            initial={{ width: 0 }}
            animate={{ width: `${progress}%` }}
            transition={{ duration: 0.3 }}
          />
        </div>
      </div>

      {/* Question Card */}
      <AnimatePresence mode="wait">
        <motion.div
          key={currentQuestion}
          initial={{ opacity: 0, x: 50 }}
          animate={{ opacity: 1, x: 0 }}
          exit={{ opacity: 0, x: -50 }}
          className="bg-slate-800/50 backdrop-blur rounded-2xl p-6 border border-slate-700/50"
        >
          {/* Topic Badge */}
          <div className="inline-flex items-center gap-2 px-3 py-1 bg-teal-500/10 border border-teal-500/20 rounded-full mb-4">
            <span className="text-teal-400 text-sm font-medium">
              {currentQ.topic_name || t('quiz.general', 'General')}
            </span>
          </div>

          {/* Question Image (if available) - FULL IMAGE, NO CROPPING */}
          {currentQ.image_url && (
            <motion.div
              initial={{ opacity: 0, scale: 0.9 }}
              animate={{ opacity: 1, scale: 1 }}
              className="mb-4 rounded-xl overflow-hidden border border-slate-700 bg-slate-900"
            >
              <img 
                src={resolveImageUrl(currentQ.image_url)} 
                alt={t('quiz.imageAlt')} 
                className="w-full max-h-64 object-contain mx-auto"
                onError={(e) => { e.target.style.display = 'none'; }}
              />
            </motion.div>
          )}

          {/* Question Text */}
          <motion.h2 
            className="text-xl font-semibold text-white mb-6"
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
          >
            {currentQ.text}
          </motion.h2>

          {/* Options */}
          <div className="space-y-3">
            {currentQ.options.map((option, idx) => {
              const optionKey = option.key || String.fromCharCode(65 + idx); // A, B, C, D
              const optionLabel = typeof option === 'string' ? option : (option.label || option.text || option);
              const isSelected = answers[currentQ._id] === optionKey;
              
              return (
                <motion.button
                  key={idx}
                  initial={{ opacity: 0, y: 10 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: idx * 0.1 }}
                  whileHover={{ scale: 1.01 }}
                  whileTap={{ scale: 0.99 }}
                  onClick={() => handleAnswer(currentQ._id, optionKey)}
                  disabled={submitting}
                  className={`w-full p-4 rounded-xl text-left transition-all flex items-center gap-4 ${
                    isSelected
                      ? 'bg-teal-500/20 border-2 border-teal-500 text-white'
                      : 'bg-slate-700/50 border-2 border-slate-600 text-slate-300 hover:border-teal-500/50 hover:bg-slate-700'
                  }`}
                  data-testid={`option-${optionKey}`}
                >
                  <span className={`w-10 h-10 rounded-lg flex items-center justify-center font-bold ${
                    isSelected
                      ? 'bg-teal-500 text-white'
                      : 'bg-slate-600 text-slate-300'
                  }`}>
                    {optionKey}
                  </span>
                  <span className="flex-1 font-medium">{optionLabel}</span>
                  {isSelected && <CheckCircle className="w-5 h-5 text-teal-400" />}
                </motion.button>
              );
            })}
          </div>
        </motion.div>
      </AnimatePresence>

      {/* Navigation */}
      <div className="flex justify-between items-center">
        <Button
          onClick={handlePrevious}
          disabled={currentQuestion === 0}
          variant="outline"
          className="bg-transparent border-slate-600 text-slate-300 hover:bg-slate-800 hover:text-white"
        >
          <ArrowLeft className="w-4 h-4 mr-2" />
          {t('quiz.previous')}
        </Button>

        {currentQuestion === quiz.questions.length - 1 ? (
          <Button
            onClick={handleSubmit}
            disabled={submitting}
            className="bg-gradient-to-r from-teal-500 to-cyan-500 hover:from-teal-600 hover:to-cyan-600"
            data-testid="submit-quiz-button"
          >
            {submitting ? t('quiz.submitting') : t('quiz.submit')}
          </Button>
        ) : (
          <Button
            onClick={handleNext}
            className="bg-gradient-to-r from-teal-500 to-cyan-500 hover:from-teal-600 hover:to-cyan-600"
          >
            {t('quiz.next')}
            <ArrowRight className="w-4 h-4 ml-2" />
          </Button>
        )}
      </div>

      {/* Question Indicators */}
      <div className="flex flex-wrap justify-center gap-2 mt-4">
        {quiz.questions.map((q, idx) => (
          <button
            key={idx}
            onClick={() => setCurrentQuestion(idx)}
            className={`w-8 h-8 rounded-lg text-xs font-bold transition-all ${
              idx === currentQuestion
                ? 'bg-teal-500 text-white scale-110'
                : answers[q._id]
                ? 'bg-teal-500/30 text-teal-400'
                : 'bg-slate-700 text-slate-400 hover:bg-slate-600'
            }`}
          >
            {idx + 1}
          </button>
        ))}
      </div>
    </div>
  );
}
